import React, { useMemo, useState } from 'react';

import { getWorkflowRoute } from '@aline/common';
import { link } from '@aline/routes';
import { Skeleton, Typography } from '@mui/material';
import { matchSorter } from 'match-sorter';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SearchInput } from 'src/components';
import { AlineButton } from 'src/components/generic';
import { QuickStartTemplateIcon } from 'src/components/icons/QuickStartTemplateIcon';
import { Apollo } from 'src/gql/generated';
import type { CreateWorkflowInstanceArgs } from 'src/hooks';
import { useGetPublishedTemplateWorkflows } from 'src/hooks';
import { useCreateWorkflow } from 'src/workflow';

import { useBodyStyles } from './LaunchTemplateBody';
import { StepLayout } from '../StepLayout';

export type TemplateQuickStartState = Omit<CreateWorkflowInstanceArgs, 'templateWorkflowId'> & {
  excludedDocumentIds: UUID[];
};
export function TemplateWorkflowsList({ templateWorkflowId }: { templateWorkflowId: UUID | null }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState<string>('');
  const { classes } = useBodyStyles({ isTemplateSelected: false });
  const { loading, publishedTemplateWorkflows } = useGetPublishedTemplateWorkflows({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const { createWorkflow, loading: creatingWorkflow } = useCreateWorkflow();
  const filteredTemplateWorkflows = useMemo(() => {
    if (!search)
      return [...publishedTemplateWorkflows].sort((a, b) => {
        const aName = a.name || 'Untitled';
        const bName = b.name || 'Untitled';

        return aName.localeCompare(bName);
      });

    return matchSorter(publishedTemplateWorkflows, search, {
      keys: ['name'] satisfies Array<keyof Apollo.Workflow>,
    });
  }, [publishedTemplateWorkflows, search]);
  const stepLayoutTitle = 'Select a template';

  if (loading) {
    return (
      <div className={classes.main}>
        <div className={classes.stepsContainer}>
          <StepLayout title={stepLayoutTitle}>
            <SearchInput placeholder="Search templates..." search={search} setSearch={setSearch} />
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className={classes.template}>
                <div>
                  <QuickStartTemplateIcon />
                </div>
                <div className={classes.templateTextContainer}>
                  <Typography variant="subtitle1">
                    <Skeleton variant="text" width={100} />
                  </Typography>
                  <Typography variant="body2">
                    <Skeleton variant="text" width={160} />
                  </Typography>
                </div>
              </div>
            ))}
          </StepLayout>
        </div>
      </div>
    );
  }

  if (!filteredTemplateWorkflows.length) {
    return (
      <div className={classes.main}>
        <div className={classes.stepsContainer}>
          <StepLayout title={stepLayoutTitle}>
            <SearchInput placeholder="Search templates..." search={search} setSearch={setSearch} />
            <div className={classes.noTemplates}>
              <Typography variant="subtitle2">No templates found</Typography>
              {!publishedTemplateWorkflows.length && (
                <AlineButton
                  color="primary"
                  loading={creatingWorkflow}
                  variant="outlined"
                  onClick={async () => {
                    const { data } = await createWorkflow({
                      variables: {
                        input: { documentIds: [], props: { isTemplate: true } },
                      },
                      variant: Apollo.WorkflowVariant.Template,
                    });
                    if (!data) return;

                    navigate(getWorkflowRoute(data.initializeWorkflow.workflow.id));
                  }}
                >
                  Create template
                </AlineButton>
              )}
            </div>
          </StepLayout>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.main}>
      <div className={classes.stepsContainer}>
        <StepLayout title={stepLayoutTitle}>
          <SearchInput placeholder="Search templates..." search={search} setSearch={setSearch} />
          {filteredTemplateWorkflows.map(templateWorkflow => {
            return (
              <TemplateWorkflowItem
                key={templateWorkflow.id}
                templateWorkflow={templateWorkflow}
                templateWorkflowId={templateWorkflowId}
              />
            );
          })}
        </StepLayout>
      </div>
    </div>
  );
}

interface WorkflowFileWithDocument extends Pick<Apollo.WorkflowFile, 'id' | '__typename'> {
  document: Pick<Apollo.Document, 'id' | 'name' | '__typename'>;
}
interface TemplateWorkflowWithFiles extends Pick<Apollo.Workflow, 'id' | 'name' | '__typename'> {
  files: WorkflowFileWithDocument[];
}

function TemplateWorkflowItem({
  templateWorkflow,
  templateWorkflowId,
}: {
  templateWorkflow: TemplateWorkflowWithFiles;
  templateWorkflowId: UUID | null;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isTemplateSelected = templateWorkflow.id === templateWorkflowId;
  const { classes } = useBodyStyles({ isTemplateSelected });

  const getSearchParamsString = () => {
    const oppId = searchParams.get('oppId');
    const accountId = searchParams.get('accountId');
    const service = searchParams.get('service');

    const params = new URLSearchParams();
    if (oppId) params.set('oppId', oppId);
    if (accountId) params.set('accountId', accountId);
    if (service) params.set('service', service);

    const str = params.toString();
    return str ? `?${str}` : '';
  };

  return (
    <div
      className={classes.template}
      onClick={() => {
        const queryString = getSearchParamsString();
        if (isTemplateSelected) {
          return navigate(`${link.QuickStartLaunchTemplateIndex()}${queryString}`);
        }

        navigate(
          `${link.QuickStartLaunchTemplateSelectedTemplate({ templateWorkflowId: templateWorkflow.id })}${queryString}`
        );
      }}
    >
      <div>
        <QuickStartTemplateIcon />
      </div>
      <div className={classes.templateTextContainer}>
        <Typography variant="subtitle1">{templateWorkflow.name || 'Untitled'}</Typography>
        <Typography variant="body2">
          {templateWorkflow.files.map(f => f.document.name).join(', ')}
        </Typography>
      </div>
    </div>
  );
}
